import React, { useEffect, useState } from "react";
import { Col, Row, Spinner } from "react-bootstrap";
import AssignRoleToUser from "./AssignRoleToUser";
import useRoles from "hooks/access-control/useRoles";
import usePrivileges from "hooks/access-control/usePrivileges";
import AssignPrivilegesToUser from "./AssignPrivilegesToUser";
import { toast } from "react-toastify";
import HeaderBar from "./HeaderBar";
import ModeSwitcher from "./ModeSwitcher";
import UserSelector from "./UserSelector";
import ActionButtons from "./ActionButtons";
import { useUserManagement } from "hooks";

export default function AssignRole() {
  const { searchUserProfiles, loading: usersLoading } = useUserManagement();
  const { roles, fetchUserRoles, assignRolesToUser, removeRolesFromUser } =
    useRoles();
  const {
    fetchUserPrivileges,
    assignPrivilegesToUser,
    removePrivilegesFromUser,
  } = usePrivileges();
  const [userAssignedRoles, setUserAssignedRoles] = useState([]);
  const [userAssignedPrivileges, setUserAssignedPrivileges] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [mode, setMode] = useState("roles");
  const [removedItemIds, setRemovedItemIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Form fields
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedPrivileges, setSelectedPrivileges] = useState([]);

  useEffect(() => {
    // fetch all user assigned roles
    const fetchAssignedUserRoles = async () => {
      if (!selectedUser) return;
      setIsFetching(true);
      try {
        const userRolesResponse = await fetchUserRoles(selectedUser.value);
        const mappedRoles = userRolesResponse
          .map((userRole) => roles.find((role) => role.id === userRole.roleId))
          .filter(Boolean);

        setUserAssignedRoles(mappedRoles);
        setSelectedRoles(
          mappedRoles?.map((role) => ({
            value: role.id,
            label: role.name,
          })),
        );
        setSelectedRoles([]);
        setRemovedItemIds([]);
        setDisabled(false);
      } catch (error) {
        console.log("failed to fetch user roles:", error);
      } finally {
        setIsFetching(false);
      }
    };

    fetchAssignedUserRoles();
  }, [selectedUser, fetchUserRoles, roles]);

  // fetch all user assigned privileges
  useEffect(() => {
    const loadPrivileges = async () => {
      if (!selectedUser) return;
      try {
        const res = await fetchUserPrivileges(selectedUser.value);

        const mapped = res.data.map((p) => ({
          value: p.privilege.id,
          label: p.privilege.name,
        }));

        setUserAssignedPrivileges(res.data);
        setSelectedPrivileges([]);
        setRemovedItemIds([]);
        setDisabled(false);
      } catch (error) {
        console.log("Error loading privileges:", error);
      }
    };

    loadPrivileges();
  }, [selectedUser, fetchUserPrivileges, mode]);

  useEffect(() => {
    if (!selectedUser) {
      setUserAssignedRoles([]);
      setSelectedRoles([]);
      setSelectedPrivileges([]);
      setDisabled(true);
    }
  }, [selectedUser]);

  // handles roles added or removed via the transfer component
  const handleRolesChange = ({ added, removed }) => {
    setSelectedRoles(added); // new roles to assign
    setRemovedItemIds(removed); // roles to remove
  };

  const handlePrivilegesChange = ({ added, removed }) => {
    setSelectedPrivileges(added); // privileges to assign
    setRemovedItemIds(removed); // privileges to remove
  };

  const handleSaveRoles = async () => {
    setSubmitting(true);
    try {
      // assign new roles
      if (selectedRoles.length > 0) {
        await assignRolesToUser(selectedUser.value, selectedRoles);
      }

      // remove roles
      if (removedItemIds.length > 0) {
        await removeRolesFromUser(selectedUser.value, removedItemIds);
      }

      // refresh the assigned roles for UI
      const res = await fetchUserRoles(selectedUser.value);
      const mappedRoles = res
        .map((userRole) => roles.find((role) => role.id === userRole.roleId))
        .filter(Boolean);

      setUserAssignedRoles(mappedRoles);

      // reset selections
      setSelectedRoles([]);
      setRemovedItemIds([]);
      setDisabled(true);
    } catch (error) {
      console.log(error);
      toast.error("Failed to update roles");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSavePrivileges = async () => {
    setSubmitting(true);
    try {
      // assign new privileges
      if (selectedPrivileges.length > 0) {
        await assignPrivilegesToUser(selectedUser.value, selectedPrivileges);
      }

      // remove privileges
      if (removedItemIds.length > 0) {
        await removePrivilegesFromUser(selectedUser.value, removedItemIds);
      }

      // refresh assigned privileges
      const res = await fetchUserPrivileges(selectedUser.value);
      setUserAssignedPrivileges(res.data);

      // reset selections
      setSelectedPrivileges([]);
      setRemovedItemIds([]);
      setDisabled(true);
    } catch (error) {
      console.log(error);
      toast.error("Failed to update privileges");
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <>
      <HeaderBar />

      <Row className="rounded-3 px-lg-4 relative">
        <ModeSwitcher mode={mode} setMode={setMode} />

        <UserSelector
          searchUserProfiles={searchUserProfiles}
          loading={usersLoading}
          selectedUser={selectedUser}
          setSelectedUser={setSelectedUser}
        />
      </Row>

      {isFetching ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: 300 }}
        >
          <Spinner animation="border" variant="success" />
          <span className="ms-3" style={{ fontSize: "13.33px" }}>
            Loading...
          </span>
        </div>
      ) : (
        <Row className="pb-lg-3 rounded-3 px-lg-4 mt-3">
          <ActionButtons
            mode={mode}
            selectedUser={selectedUser}
            disabled={disabled}
            setDisabled={setDisabled}
            handleSaveRoles={handleSaveRoles}
            handleSavePrivileges={handleSavePrivileges}
            submitting={submitting}
          />

          <Col className="col-12">
            {mode === "roles" && (
              <AssignRoleToUser
                disabled={disabled}
                onRolesChange={handleRolesChange}
                assignedRoles={userAssignedRoles}
              />
            )}

            {mode === "privileges" && (
              <AssignPrivilegesToUser
                disabled={disabled}
                onPrivilegesChange={handlePrivilegesChange}
                assignedPrivileges={userAssignedPrivileges}
              />
            )}
          </Col>
        </Row>
      )}
    </>
  );
}
